/*
 * 顶部付费入口 Banner（webVipEntrance AB 实验组）
 * 替代非 SVIP 团队的顶部「Statistics + 开通/升级按钮」组合，按身份×存储×临期×补差价的状态机渲染。
 * SVIP 团队继续走原 NewStatistics + BussinessArea 视觉，不进入本组件。
 */
import React, { useEffect, useMemo, useState } from 'react'
import {
  useAppDispatch,
  useAppSelector,
  useVip,
  useVipKind,
  useEffectiveStorage,
  useEffectiveExpireDays,
  useEffectiveCanDiffUpgrade
} from '../../../app/hooks'
import { fetchUpdateGoodsInfo } from '../../../actions/payment'
import {
  storageEntranceCustomContent,
  StorageStage
} from '../storageEntranceVariant'
import CONFIG from '../../../constants/config'
import crownIcon from './images/vip_entrance_crown.svg?url'
import warningIcon from './images/vip_entrance_warning.svg?url'
import './VipEntranceBanner.scss'

interface VipEntranceBannerProps {
  /** 主按钮点击 · 由 Header 透传（复用现有 newOnPay(false) 的临期续费/推荐等级路由）
   *  · 第二参 customContent 仅在存储告警态由 banner 内部计算后传入 */
  onPay: (customContent?: { variant: string; data: any } | null) => void
  /** 距到期天数 · 由 Header 算好传入 · 负数为已过期 */
  expireDays: number
}

const labelForKind = (vipKind: number): string => {
  if (vipKind === CONFIG.VIP_KIND.SVIP) return '超级会员'
  if (vipKind === CONFIG.VIP_KIND.VVIP) return '高级会员'
  if (vipKind === CONFIG.VIP_KIND.VIP) return '基础会员'
  return ''
}

interface BannerDisplay {
  /** 主文案前半（普通色） */
  titlePrefix?: string
  /** 主文案中间（高亮色） */
  titleHighlight?: string
  /** 主文案后半（普通色） */
  titleSuffix?: string
  /** 副文案 */
  subtitle: string
  /** 按钮文案 */
  btnText: string
  /** 视觉基调 · 'warning' = 存储告警（橙图标+红高亮）· 缺省 = 营销态（金图标+金高亮） */
  tone?: 'warning'
}

// 1-30 天算临期，到 PRD 1.4「到期前30天-1天」
const EXPIRING_DAYS_MAX = 30
// 存储用量阈值
const STORAGE_NEAR_RATIO = 0.9

const VipEntranceBanner: React.FC<VipEntranceBannerProps> = ({
  onPay,
  expireDays: realExpireDays
}) => {
  const dispatch = useAppDispatch()
  const group = useAppSelector((state) => state.group)
  const { isVip, isSVip, isVVip } = useVip()
  const vipKind = useVipKind()

  // 补差价升级资格 + 最低差价 · 真实值来自后端 /web/order/update/good/info
  const [realCanDiffUpgrade, setRealCanDiffUpgrade] = useState(false)
  // 补差价最低价（元）· 取 goodsInfo 里各档 price 的最小值，用于文案「X 元起补差价升级」；
  // 拿不到时为 null，文案回退到固定「补差价升级」话术
  const [minUpgradePrice, setMinUpgradePrice] = useState<number | null>(null)
  const groupId = group.groupId
  useEffect(() => {
    if (!isVip || isSVip) {
      setRealCanDiffUpgrade(false)
      setMinUpgradePrice(null)
      return
    }
    dispatch(fetchUpdateGoodsInfo({ groupID: groupId }))
      .then((data: any) => {
        const goodsInfo: Array<{ price?: number }> = data?.goodsInfo ?? []
        setRealCanDiffUpgrade(goodsInfo.length > 0)
        // price 单位为元（弹窗 panel 直接渲染 ¥price，无 /100）· 取有效正价的最小值
        const prices = goodsInfo
          .map((g) => g?.price)
          .filter((p): p is number => typeof p === 'number' && p > 0)
        setMinUpgradePrice(prices.length ? Math.min(...prices) : null)
      })
      .catch(() => {
        setRealCanDiffUpgrade(false)
        setMinUpgradePrice(null)
      })
  }, [isVip, isSVip, groupId, dispatch])

  const canDiffUpgrade = useEffectiveCanDiffUpgrade(realCanDiffUpgrade)
  const expireDays = useEffectiveExpireDays(realExpireDays)
  const { totalNum, limitPhotoNum, viewLimited } = useEffectiveStorage()
  const storageRatio = limitPhotoNum > 0 ? totalNum / limitPhotoNum : 0

  const display: BannerDisplay = useMemo(() => {
    // 1. 过期（expireDays < 0）· 4 态
    if (expireDays < 0) {
      if (storageRatio >= 1 && viewLimited) {
        return {
          titleHighlight: '存储已满',
          titleSuffix: '，超限照片无法查看',
          subtitle: '续费会员恢复查看',
          btnText: '续费会员',
          tone: 'warning'
        }
      }
      if (storageRatio >= 1) {
        return {
          titleHighlight: '存储已满',
          titleSuffix: '，超限照片将无法查看',
          subtitle: '续费会员恢复更多存储空间',
          btnText: '续费会员',
          tone: 'warning'
        }
      }
      if (storageRatio >= STORAGE_NEAR_RATIO) {
        const remain = Math.max(1, Math.ceil((1 - storageRatio) * 100))
        return {
          titleHighlight: `存储仅剩 ${remain}%`,
          titleSuffix: '，超限照片将无法查看',
          subtitle: '续费会员恢复更多存储空间',
          btnText: '续费会员',
          tone: 'warning'
        }
      }
      return {
        titlePrefix: '会员已过期，续费即恢复权益',
        subtitle: '更多存储·无限查看·批量下载',
        btnText: '续费会员'
      }
    }

    // 2. 临期 VIP/VVIP/SVIP（1-30 天）· 倒数态，存储不细分
    if (isVip && expireDays >= 0 && expireDays <= EXPIRING_DAYS_MAX) {
      const dayText = expireDays === 0 ? '今日' : `${expireDays} 天后`
      return {
        titlePrefix: '会员',
        titleHighlight: dayText,
        titleSuffix: '到期',
        subtitle: '续费继续享照片查看、批量下载等 30+ 权益',
        btnText: '续费会员'
      }
    }

    // 3. 在期 VIP/VVIP（非临期）· 存储优先 > 补差价 > 默认
    if (isVip) {
      if (storageRatio >= 1) {
        return {
          titleHighlight: '存储已满',
          titleSuffix: '，超限照片将无法查看',
          subtitle: '升级会员获得更多存储空间',
          btnText: '升级会员',
          tone: 'warning'
        }
      }
      if (storageRatio >= STORAGE_NEAR_RATIO) {
        const remain = Math.max(1, Math.ceil((1 - storageRatio) * 100))
        return {
          titleHighlight: `存储仅剩 ${remain}%`,
          titleSuffix: '，超限照片将无法查看',
          subtitle: '升级会员获得更多存储空间',
          btnText: '升级会员',
          tone: 'warning'
        }
      }
      if (canDiffUpgrade) {
        return {
          titlePrefix: minUpgradePrice
            ? `${minUpgradePrice} 元起补差价升级，解锁更多权益`
            : '补差价升级，解锁更多权益',
          subtitle: '更多存储·照片分类·团队管理',
          btnText: '升级会员'
        }
      }
      return {
        titlePrefix: '升级会员解锁更多权益',
        subtitle: '更多存储·照片分类·团队管理',
        btnText: '升级会员'
      }
    }

    // 4. 非会员 · 4 态
    if (storageRatio >= 1 && viewLimited) {
      return {
        titleHighlight: '存储已满',
        titleSuffix: '，超限照片无法查看',
        subtitle: '开通会员恢复查看',
        btnText: '开通会员',
        tone: 'warning'
      }
    }
    if (storageRatio >= 1) {
      return {
        titleHighlight: '存储已满',
        titleSuffix: '，超限照片将无法查看',
        subtitle: '开通会员获得更多存储空间',
        btnText: '开通会员',
        tone: 'warning'
      }
    }
    if (storageRatio >= STORAGE_NEAR_RATIO) {
      const remain = Math.max(1, Math.ceil((1 - storageRatio) * 100))
      return {
        titleHighlight: `存储仅剩 ${remain}%`,
        titleSuffix: '，超限照片将无法查看',
        subtitle: '开通会员获得更多存储空间',
        btnText: '开通会员',
        tone: 'warning'
      }
    }
    return {
      titlePrefix: '17 元/月起开通会员，尽享 30+ 权益',
      subtitle: '更多存储·无限查看·批量下载',
      btnText: '开通会员'
    }
  }, [
    expireDays,
    isVip,
    storageRatio,
    viewLimited,
    canDiffUpgrade,
    minUpgradePrice,
    isVVip,
    vipKind
  ])

  const isWarning = display.tone === 'warning'

  // PRD 03：顶部入口仅在「存储告警态」(near/over_safe/over_limited) 替换弹窗标题/副标题
  const handleBannerClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!isWarning) {
      onPay()
      return
    }
    let stage: StorageStage
    if (storageRatio < 1) {
      stage = 'near'
    } else if (!isVip && viewLimited) {
      stage = 'over_limited'
    } else {
      stage = 'over_safe'
    }
    const remainPercent =
      stage === 'near'
        ? Math.max(1, Math.ceil((1 - storageRatio) * 100))
        : undefined
    onPay(
      storageEntranceCustomContent({
        stage,
        isMember: !!isVip,
        totalNum,
        limitPhotoNum,
        remainPercent,
        levelName: labelForKind(vipKind)
      })
    )
  }

  return (
    <div
      className={`vip-entrance-banner ${isWarning ? 'is-warning' : ''}`.trim()}
      onClick={handleBannerClick}
      role="button"
    >
      <img
        className="vip-entrance-banner__icon"
        src={isWarning ? warningIcon : crownIcon}
        alt=""
        draggable={false}
      />
      <div className="vip-entrance-banner__text">
        <div className="vip-entrance-banner__title">
          {display.titlePrefix}
          {display.titleHighlight && (
            <span className="vip-entrance-banner__highlight">
              {display.titleHighlight}
            </span>
          )}
          {display.titleSuffix}
        </div>
        <div className="vip-entrance-banner__subtitle">{display.subtitle}</div>
      </div>
      <button
        type="button"
        className="vip-entrance-banner__btn"
        onClick={handleBannerClick}
      >
        {display.btnText}
      </button>
    </div>
  )
}

export default VipEntranceBanner
